/**
 * Use this for strict typing a defined value for the undefined & null check.
 * For example values from the C Sharp JSON serializer,
 * default to null despite swagger docs, so this needs to be handled specifically.
 */
export function isDefined<T> (val: T | undefined | null): val is T {
    return val !== undefined && val !== null;
}

/**
 * Use to cover defined but empty string checks aka ''.
 */
export function isEmpty (value: unknown): value is string {
    return typeof value === 'string' && value.length === 0;
}

/**
 * Use to check for a string that is not empty.
 */
export function isNonEmptyString (value: unknown): value is string {
    return typeof value === 'string' && value.length > 0;
}

// Not truly random, please don't use for anything that needs any security
export function getRandomNumBetween (min: number, max: number) {
    return Math.random() * (max - min) + min;
}

// Not truly random, please don't use for anything that needs any security
export function getRandomStringOfLength (length: number): string {
    let result = '';

    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const charactersLength = characters.length;

    for (let i = 0; i < length; i += 1) {
        result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }

    return result;
}

export function getIsRunningOnDevelopersMachine (): boolean {
    return /^https?:\/\/localhost|0\.0\.0\.0/.test(window.location.toString());
}

/**
 * Determine if the current runtime is the Jest test runner,
 * this env is specified in the jest docs here.
 *
 * @see https://jestjs.io/docs/environment-variables
 * @see https://stackoverflow.com/a/52231746
 */
export function isInJest (): boolean {
    return window?.process?.env?.JEST_WORKER_ID !== undefined;
}

export const pDelay = (time = 500) => {
    return new Promise<void>((resolve) => setTimeout(() => resolve(), time));
};

/**
 * Normalized state transformer, as suggested in redux docs.
 *
 * @external https://redux.js.org/usage/structuring-reducers/normalizing-state-shape#designing-a-normalized-state
 */
type GroupedById<T> = {
    byId: {
        [key: number]: T
    }
    allIds: Array<number>
};

export function groupById<T extends { id: number }> (list: Array<T> = []): GroupedById<T> {
    const allIds = list.map(item => item.id);
    const byId: { [id: number]: T } = {};

    list.forEach(item => {
        byId[item.id] = item;
    });

    return {
        allIds,
        byId
    };
}

export function minutesToMilliseconds (minutes: number) {
    return minutes * (60 * 1000);
}

export function minutesToSeconds (minutes: number) {
    return minutes * 60;
}

export function secondsToMinutes (seconds: number) {
    return seconds / 60;
}

/**
 * Intentionally ignore parse errors of a value to a number.
 * For using with config values from an untrusted serialized source.
 */
export function tryParseNumber (value: any): null | number {
    if (typeof value === 'number') {
        return value;
    }

    try {
        const result = parseInt(value);

        if (isNaN(result)) {
            return null;
        }

        return result;
        // eslint-disable-next-line no-empty
    } catch {
    }

    return null;
}

/**
 * Intentionally ignore parse errors of a value to a float.
 * For using with config values from an untrusted serialized source.
 */
export function tryParseFloat (value: any): null | number {
    if (typeof value === 'number') {
        return value;
    }

    try {
        const result = parseFloat(value);

        if (isNaN(result)) {
            return null;
        }

        return result;
        // eslint-disable-next-line no-empty
    } catch {
    }

    return null;
}

/**
 * Helper to derive a number from a URL param by a key name.
 */
export function tryNumberFromURLParam (key: string,
    href: string = window.location.href): number | null {
    const locationURL = new URL(href);
    const value = locationURL.searchParams.get(key);
    const numberValue = tryParseNumber(value);

    return numberValue;
}

/**
 * Helper to derive a number from a URL param by a key name.
 */
export function tryValueFromURLParam (
    key: string,
    href: string = window.location.href
): string | null {
    const locationURL = new URL(href);
    const value = locationURL.searchParams.get(key);

    return value;
}

/**
 * A typesafe utlity to check for the existance of a property.
 * This is useful in unknown types and conditions with Unions.
 */
export function hasOwnProperty<TValue extends Record<string, unknown>, TKey extends PropertyKey> (
    object: TValue, key: TKey
): object is TValue & Record<TKey, unknown> {
    return Object.prototype.hasOwnProperty.call(object, key);
}

/**
 * Utility to create a type that represents all of the property types.
 * Example:
 * const Object = {a: 1, b:2}
 * type Values = ValueOf<typeof Object> // 'a' | 'b'.
 */
export type PropertyTypesOf<TObject> = keyof TObject;

/**
 * Check if valid url.
 */
export function isValidUrl (urlString: string) {
    let url: URL;

    try {
        url = new URL(urlString);
    } catch (e) {
        return false;
    }

    return url.protocol === 'http:' || url.protocol === 'https:';
}
